import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './Tooltip.css';

const Tooltip = ({ content, children, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const computeStyle = useCallback(() => {
    if (!triggerRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const TOOLTIP_OFFSET = 8;
    const TOOLTIP_MAX_WIDTH = 300;
    const TOOLTIP_APPROX_HEIGHT = 40;

    let effectivePosition = position;

    if (position === 'top' && triggerRect.top < TOOLTIP_APPROX_HEIGHT + TOOLTIP_OFFSET) {
      effectivePosition = 'bottom';
    } else if (position === 'bottom' && triggerRect.bottom + TOOLTIP_APPROX_HEIGHT + TOOLTIP_OFFSET > vh) {
      effectivePosition = 'top';
    } else if (position === 'left' && triggerRect.left < TOOLTIP_MAX_WIDTH + TOOLTIP_OFFSET) {
      effectivePosition = 'right';
    } else if (position === 'right' && triggerRect.right + TOOLTIP_MAX_WIDTH + TOOLTIP_OFFSET > vw) {
      effectivePosition = 'left';
    }

    let style = {};
    switch (effectivePosition) {
      case 'top':
        style = {
          top: triggerRect.top + scrollY - TOOLTIP_OFFSET,
          left: triggerRect.left + scrollX + triggerRect.width / 2,
          transform: 'translate(-50%, -100%)'
        };
        break;
      case 'bottom':
        style = {
          top: triggerRect.bottom + scrollY + TOOLTIP_OFFSET,
          left: triggerRect.left + scrollX + triggerRect.width / 2,
          transform: 'translateX(-50%)'
        };
        break;
      case 'left':
        style = {
          top: triggerRect.top + scrollY + triggerRect.height / 2,
          left: triggerRect.left + scrollX - TOOLTIP_OFFSET,
          transform: 'translate(-100%, -50%)'
        };
        break;
      case 'right':
        style = {
          top: triggerRect.top + scrollY + triggerRect.height / 2,
          left: triggerRect.right + scrollX + TOOLTIP_OFFSET,
          transform: 'translateY(-50%)'
        };
        break;
      default:
        style = {
          top: triggerRect.top + scrollY - TOOLTIP_OFFSET,
          left: triggerRect.left + scrollX + triggerRect.width / 2,
          transform: 'translate(-50%, -100%)'
        };
    }

    setTooltipStyle(style);
  }, [position]);

  useEffect(() => {
    if (isVisible) {
      computeStyle();
      window.addEventListener('scroll', computeStyle, true);
      window.addEventListener('resize', computeStyle);
      return () => {
        window.removeEventListener('scroll', computeStyle, true);
        window.removeEventListener('resize', computeStyle);
      };
    }
  }, [isVisible, computeStyle]);

  const tooltipContent = isVisible && content ? ReactDOM.createPortal(
    <div 
      ref={tooltipRef}
      className={`tooltip-popup tooltip-popup-${position}`}
      style={tooltipStyle}
    >
      {content}
    </div>,
    document.body
  ) : null;

  return (
    <span 
      ref={triggerRef}
      className="tooltip-trigger"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {tooltipContent}
    </span>
  );
};

export default Tooltip;